import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CreditCard, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import payappService from '../services/payappService';
import authService from '../services/authService';
import supabaseService from '../services/supabaseService';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(8px);
`;

const ModalContainer = styled(motion.div)`
  background: ${props => props.theme === 'dark' ? '#1a1a1a' : '#ffffff'};
  border-radius: 16px;
  padding: 32px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  border: 1px solid ${props => props.theme === 'dark' ? '#333' : '#e0e0e0'};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${props => props.theme === 'dark' ? '#ffffff' : '#1a1a1a'};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme === 'dark' ? '#888' : '#666'};
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme === 'dark' ? '#333' : '#f5f5f5'};
    color: ${props => props.theme === 'dark' ? '#fff' : '#000'};
  }
`;

const ChargeOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`;

const ChargeOption = styled(motion.button)`
  background: ${props => props.selected ? '#007AFF' : (props.theme === 'dark' ? '#2a2a2a' : '#f8f9fa')};
  border: 2px solid ${props => props.selected ? '#007AFF' : (props.theme === 'dark' ? '#333' : '#e0e0e0')};
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  width: 100%;
  
  &:hover {
    border-color: #007AFF;
    background: ${props => props.theme === 'dark' ? '#333' : '#f0f8ff'};
  }
`;

const OptionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const OptionLabel = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.selected ? '#ffffff' : (props.theme === 'dark' ? '#ffffff' : '#1a1a1a')};
`;

const OptionDescription = styled.div`
  font-size: 14px;
  color: ${props => props.selected ? '#e6f3ff' : (props.theme === 'dark' ? '#888' : '#666')};
`;

const PayButton = styled(motion.button)`
  background: #007AFF;
  color: white;
  border: none;
  border-radius: 12px;
  padding: 16px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #0056CC;
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const StatusMessage = styled(motion.div)`
  padding: 16px;
  border-radius: 8px;
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  
  &.success {
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  }
  
  &.error {
    background: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  }
  
  &.info {
    background: #d1ecf1;
    color: #0c5460;
    border: 1px solid #bee5eb;
  }
`;

const ChargeModal = ({ isOpen, onClose, theme = 'light' }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const chargeOptions = payappService.getChargeOptions();

  // 결제 완료 감지 및 실시간 포인트 동기화
  useEffect(() => {
    if (!isOpen) return;

    const refreshPointsFromServer = async () => {
      try {
        console.log('💳 ChargeModal: 서버에서 포인트 새로고침 시작');
        
        // 여러 방법으로 사용자 정보 가져오기
        let currentUser = authService.getSession()?.user;
        console.log('💳 ChargeModal: authService 사용자:', currentUser?.email);
        
        // authService가 실패하면 localStorage에서 직접 가져오기
        if (!currentUser?.email) {
          const accountData = JSON.parse(localStorage.getItem('ai_agent_account') || '{}');
          currentUser = { email: accountData.email };
          console.log('💳 ChargeModal: localStorage 사용자:', currentUser?.email);
        }
        
        if (currentUser?.email) {
          // SERVICE_ROLE_KEY를 사용한 빠른 포인트 조회
          const apiUrl = '/api/supabase-admin?path=users%3Femail%3Deq.' + encodeURIComponent(currentUser.email) + '%26select%3D*&method=GET&api=rest';
          console.log('💳 ChargeModal: API 호출:', apiUrl);
          
          const response = await fetch(apiUrl);
          console.log('💳 ChargeModal: API 응답 상태:', response.status);
          
          if (response.ok) {
            const userDataArray = await response.json();
            console.log('💳 ChargeModal: 서버 데이터:', userDataArray);
            
            if (userDataArray && userDataArray.length > 0) {
              const userData = userDataArray[0];
              console.log('💳 ChargeModal: 사용자 정보:', userData);
              
              if (userData.points !== undefined) {
                console.log('💳 ChargeModal: 포인트 업데이트 중...', userData.points);
                
                // 모든 상태 즉시 업데이트
                localStorage.setItem('ai_agent_points_v1', userData.points.toString());
                
                // pointsService 업데이트
                if (window.pointsService) {
                  window.pointsService.setPoints(userData.points);
                  console.log('💳 ChargeModal: pointsService 업데이트 완료');
                }
                
                // React state 업데이트를 위한 이벤트 발생
                window.dispatchEvent(new CustomEvent('pointsUpdated', { 
                  detail: { points: userData.points } 
                }));
                
                console.log('💳 ChargeModal: 포인트 동기화 완료:', userData.points);
              }
            } else {
              console.log('💳 ChargeModal: 사용자 데이터 없음');
            }
          } else {
            console.error('💳 ChargeModal: API 호출 실패:', response.status);
          }
        } else {
          console.log('💳 ChargeModal: 로그인된 사용자 없음');
        }
      } catch (error) {
        console.error('💳 ChargeModal: 포인트 동기화 실패:', error);
      }
    };

    // 2초마다 포인트 새로고침 (결제 완료 감지)
    const interval = setInterval(refreshPointsFromServer, 2000);
    
    return () => clearInterval(interval);
  }, [isOpen]);

  const handleCharge = async () => {
    if (!selectedOption) return;

    setIsLoading(true);
    setStatus(null);

    try {
      // PayApp 결제 링크 생성
      const paymentLink = await payappService.createPaymentLink(
        selectedOption.amount,
        selectedOption.points
      );

      console.log('PayApp 결제 링크:', paymentLink);
      
      if (paymentLink.payurl) {
        // 결제 정보 세션에 저장
        sessionStorage.setItem('pending_payment_' + paymentLink.mul_no, JSON.stringify({
          mul_no: paymentLink.mul_no,
          points: selectedOption.points,
          amount: selectedOption.amount
        }));
        
        // 새 창에서 결제 페이지 열기
        window.open(
          paymentLink.payurl,
          'payapp_payment',
          'width=500,height=700,scrollbars=yes,resizable=yes'
        );
        
        // 모달 닫기
        setIsLoading(false);
        onClose();
      } else {
        throw new Error('결제 링크를 생성할 수 없습니다.');
      }

    } catch (error) {
      console.error('결제 처리 오류:', error);
      setStatus({
        type: 'error',
        message: '결제 처리 중 오류가 발생했습니다.'
      });
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <ModalOverlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <ModalContainer
          theme={theme}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Header>
            <Title theme={theme}>포인트 충전</Title>
            <CloseButton theme={theme} onClick={onClose}>
              <X size={24} />
            </CloseButton>
          </Header>

          <ChargeOptions>
            {chargeOptions.map((option) => (
              <ChargeOption
                key={option.id}
                theme={theme}
                selected={selectedOption?.id === option.id}
                onClick={() => setSelectedOption(option)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <OptionHeader>
                  <CreditCard size={20} />
                  <OptionLabel selected={selectedOption?.id === option.id}>
                    {option.label}
                  </OptionLabel>
                </OptionHeader>
                <OptionDescription selected={selectedOption?.id === option.id}>
                  {option.description}
                </OptionDescription>
              </ChargeOption>
            ))}
          </ChargeOptions>

          <PayButton
            onClick={handleCharge}
            disabled={!selectedOption || isLoading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isLoading ? (
              <>
                <Loader size={20} className="animate-spin" />
                결제 처리 중...
              </>
            ) : (
              <>
                <CreditCard size={20} />
                {selectedOption ? `${selectedOption.amount.toLocaleString()}원 결제하기` : '충전 옵션을 선택하세요'}
              </>
            )}
          </PayButton>

          {status && (
            <StatusMessage
              className={status.type}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {status.type === 'success' && <CheckCircle size={16} />}
              {status.type === 'error' && <AlertCircle size={16} />}
              {status.message}
            </StatusMessage>
          )}
        </ModalContainer>
      </ModalOverlay>
    </AnimatePresence>
  );
};

export default ChargeModal;